/**
 * 大纲方案（按背景生成，P2-2）的展示辅助：把一条改动描述成销售看得懂的话，并按勾选取出要应用的改动。
 * 纯函数，不依赖界面，便于单测。
 */
import type { OutlineOp, ProposedChange } from '../../../api/advisor';

export const KIND_META: Record<OutlineOp['kind'], { label: string; color: string }> = {
  add_topic: { label: '新增话题', color: 'green' },
  add_question: { label: '补一题', color: 'blue' },
  rewrite_question: { label: '改写', color: 'orange' },
  remove_question: { label: '删题', color: 'red' },
  remove_topic: { label: '删话题', color: 'red' },
};

export interface ChangeView {
  /** 主文案 */
  title: string;
  /** 改写前的题面（仅改写） */
  before?: string;
  /** 补充说明：位置 / 所在话题 / 将一并移除…… */
  note?: string;
  /** 新话题带的题 */
  questions?: string[];
  reason?: string;
}

export function describeChange(change: ProposedChange): ChangeView {
  const op = change.op;
  const reason = op.reason || undefined;
  switch (op.kind) {
    case 'add_topic':
      return {
        title: `「${op.title}」`,
        note: change.topicTitle ? `放在「${change.topicTitle}」之后` : '接在最后',
        questions: op.questions,
        reason,
      };
    case 'add_question':
      return { title: op.text, note: change.topicTitle ? `在「${change.topicTitle}」下` : undefined, reason };
    case 'rewrite_question':
      return {
        title: op.text,
        before: change.currentText,
        note: change.topicTitle ? `「${change.topicTitle}」` : undefined,
        reason,
      };
    case 'remove_question':
      return {
        title: change.currentText ?? '（这道题）',
        note: change.topicTitle ? `「${change.topicTitle}」` : undefined,
        reason,
      };
    case 'remove_topic':
      return {
        title: change.topicTitle ? `「${change.topicTitle}」` : '（这个话题）',
        note: '话题下还没聊过的题会一并移除',
        reason,
      };
  }
}

/** 按勾选取出要应用的改动，保持方案里的先后 */
export function selectedOps(changes: ProposedChange[], selectedIds: Iterable<string>): OutlineOp[] {
  const chosen = new Set(selectedIds);
  return changes.filter((c) => chosen.has(c.id)).map((c) => c.op);
}

/** 应用结果的一句话总结 */
export function summarizeApply(result: { applied: number; skipped: Array<unknown> }): string {
  if (result.skipped.length === 0) return `已应用 ${result.applied} 条改动`;
  return `已应用 ${result.applied} 条，${result.skipped.length} 条没能应用`;
}
